import { FilterOperator } from "api/api";
import { fetchUsers } from "api/fetchers/userList";
import { fetchUserOne } from "api/fetchers/userOne";
import { mutateUserDelete } from "api/mutations/userDelete";
import { mutateUserDisable } from "api/mutations/userDisable";
import { mutateUserEnable } from "api/mutations/userEnable";
import { mutateUserOne } from "api/mutations/userOne";
import { DataProvider } from "react-admin";
import { mutateUserInvite } from "api/mutations/userInvite";
import { convertFileToBase64 } from "reactAdmin/resource/user/convertFileToBase64";

const userDataProvider: DataProvider = {
  getList: async (resource, params) => {
    const result = await fetchUsers(
      {
        from: (params.pagination.page - 1) * params.pagination.perPage,
        to: params.pagination.page * params.pagination.perPage - 1,
      },
      [
        {
          column: params.sort.field,
          direction: params.sort.order === "DESC" ? "desc" : "asc",
        },
      ],
      Object.keys(params.filter).map((item) => {
        let operator: FilterOperator = "eq";
        let column = item;
        let value = params.filter[item];
        if (["emailAddress", "phoneNumber", "firstName", "lastName", "displayName"].indexOf(item) >= 0) {
          operator = "like";
          value = `%${value}%`;
        }
        if (item === "signedInAt-gt") {
          operator = "gt";
          column = "signedInAt";
        }
        if (item === "signedInAt-lt") {
          operator = "lt";
          column = "signedInAt";
        }
        return {column, value, operator};
      })
    );
    return { data: result.data as any, total: result.metadata.count };
  },
  getOne: async (resource, params) => {
    const id =
      typeof params.id === "string" ? parseInt(params.id, 10) : params.id;
    const result = await fetchUserOne(id);
    return { data: result as any };
  },
  getMany: async (resource, params) => {
    const result = await fetchUsers(
        {
          from: 0,
          to: 100,
        },
        undefined,
        [
          {
            column: "id",
            value: params.ids.map((item) => String(item)),
            operator: "eq",
          },
        ]
    );
    return { data: result.data as any, total: result.metadata.count };
  },
  getManyReference: async (resource, params) => {
    params.filter[params.target] = params.id;
    return userDataProvider.getList(resource, params)
  },
  create: async (resource, params) => {
    const result = await mutateUserInvite(params.data);
    return { data: { id: result.data.id, ...params.data } as any };
  },
  update: async (resource, params) => {
    if (params.data.avatar && params.data.avatar.rawFile instanceof File) {
      params.data.avatar = await convertFileToBase64(params.data.avatar.rawFile);
    } else if (params.data.avatar !== null) {
      delete params.data.avatar;
    }
    if (params.data.isDisabled) {
      await mutateUserDisable({ id: params.data.id });
    } else {
      await mutateUserEnable({ id: params.data.id });
    }
    await mutateUserOne(params.data);
    return { data: params.data as any };
  },
  updateMany: async (resource, params) => {
    for (let i = 0; i < params.ids.length; i++) {
      const id = params.ids[i];
      const parsedId = typeof id === "string" ? parseInt(id, 10) : id;
      if (params.data.enable === false) {
        await mutateUserDisable({ id: parsedId });
      } else if (params.data.enable === true) {
        await mutateUserEnable({ id: parsedId });
      }
    }
    return { data: params.ids };
  },
  delete: async (resource, params) => {
    await mutateUserDelete({
      id: typeof params.id === "string" ? parseInt(params.id, 10) : params.id,
    });
    return { data: params.previousData as any };
  },
  deleteMany: async (resource, params) => {
    for (let i = 0; i < params.ids.length; i++) {
      const id = params.ids[i];
      await mutateUserDelete({
        id: typeof id === "string" ? parseInt(id, 10) : id,
      });
    }
    return { data: params.ids };
  },
};

export default userDataProvider;
